/**
 * Zuora identity-review queue.
 *
 * These reviews are the fail-closed guard on the Zuora → CRM identity path: when the
 * resolver lands on a contact that contradicts the account an event is for, the write is
 * REFUSED and a review row is written instead. The reconciliation work for that account
 * is then held (blocked) until a human adjudicates.
 *
 * Until 2026-09-03 there was no way to read the queue at all — the table has RLS enabled
 * with zero policies and no SELECT grant to `authenticated` — so the reviews accumulated
 * with no operator surface. `get_zuora_identity_reviews` is a SECURITY DEFINER reader
 * whose admin gate mirrors `resolve_zuora_secondary_identity_review` exactly, so an
 * operator can never see work they cannot action.
 */
import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

/** Values allowed by zuora_secondary_identity_reviews_resolution_action_check. */
export const RESOLUTION_ACTIONS = [
  {
    value: "identity_corrected_in_zuora",
    label: "Identity corrected in Zuora",
    help: "The account or member number was fixed upstream. The next drain should resolve cleanly.",
  },
  {
    value: "contact_merged",
    label: "Contacts merged in the CRM",
    help: "The duplicate contact was merged into the right one.",
  },
  {
    value: "contact_corrected_in_crm",
    label: "Contact corrected in the CRM",
    help: "The contact's Zuora account id or membership id was corrected by hand.",
  },
  {
    value: "not_a_defect",
    label: "Not a defect",
    help: "The apparent conflict is legitimate and needs no change.",
  },
  {
    value: "intentional_skip_no_secondary",
    label: "Intentional skip",
    help: "Deliberately skipped. Use only when none of the above apply — this is the one action that bypasses the causal re-check.",
  },
] as const;

export type ResolutionAction = (typeof RESOLUTION_ACTIONS)[number]["value"];

export interface IdentityReview {
  id: UUID;
  reason: string;
  hold_reason: string;
  zuora_account_id: string | null;
  account_number: string | null;
  account_status: string | null;
  account_member_number: string | null;
  primary_contact_id: UUID | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_zuora_account_id: string | null;
  contact_membership_id: string | null;
  conflict_kind: string;
  occurrences: number | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  resolved_at: string | null;
  resolution_action: string | null;
  held_queue_rows: number;
  /** For a SECONDARY review: the contact that actually caused it. The review row itself stores the
   *  PRIMARY contact, so without this the UI could only show the wrong person. */
  conflicting_contact_id: UUID | null;
  conflicting_contact_name: string | null;
  conflicting_contact_email: string | null;
  conflicting_contact_account: string | null;
  secondary_member_number: string | null;
  secondary_email: string | null;
}

export interface IdentityReviewCounts {
  open_total: number;
  open_primary: number;
  open_secondary: number;
  resolved_total: number;
  held_rows: number;
}

function friendly(message?: string): string {
  if (!message) {
    return "Something went wrong.";
  }
  if (/not_authorized/i.test(message)) {
    return "You need an admin or superadmin role to work the identity-review queue.";
  }
  if (/causal_condition_still_present/i.test(message)) {
    // The resolver refuses to close a review whose cause is still live, because the next
    // drain would simply re-open it.
    return "The conflict that caused this review still exists. Fix it first, or pick the action that says what you actually did.";
  }
  if (/review_not_found_or_already_resolved/i.test(message)) {
    return "That review was already resolved — refresh to see the current queue.";
  }
  if (/resolution_action_required/i.test(message)) {
    return "Choose what was actually done before resolving.";
  }
  return message;
}

/**
 * One page of the review queue.
 *
 * This used to request 200 rows at offset 0 and stop. PROD carried 257 open reviews, so 57 of
 * them were simply invisible — a work queue that silently hides part of the work is worse than
 * no queue, because it looks finished. The caller now pages explicitly.
 */
export async function listIdentityReviews(
  includeResolved = false,
  limit = REVIEW_PAGE_SIZE,
  offset = 0,
): Promise<IdentityReview[]> {
  const { data, error } = await supabase.rpc("get_zuora_identity_reviews", {
    p_limit: limit,
    p_offset: offset,
    p_include_resolved: includeResolved,
  });
  if (error) {
    throw new Error(friendly(error.message));
  }
  return (data ?? []) as IdentityReview[];
}

export const REVIEW_PAGE_SIZE = 50;

export async function getIdentityReviewCounts(): Promise<IdentityReviewCounts> {
  const { data, error } = await supabase.rpc(
    "get_zuora_identity_review_counts",
  );
  if (error) {
    throw new Error(friendly(error.message));
  }
  return data as IdentityReviewCounts;
}

export interface ResolveResult {
  ok: boolean;
  error?: string;
  requeued_rows?: number;
  superseded_rows?: number;
  requeued_events?: number;
}

/**
 * Resolve a review and release the reconciliation rows it was holding.
 *
 * `requeue` defaults to true: the entire point of resolving is to let the held work run
 * again. The RPC releases only the rows THIS review held, and closes any duplicate as
 * superseded rather than colliding with the pending-unique index.
 */
export async function resolveIdentityReview(
  reviewId: UUID,
  action: ResolutionAction,
  note: string | null,
  requeue = true,
): Promise<ResolveResult> {
  const { data, error } = await supabase.rpc(
    "resolve_zuora_secondary_identity_review",
    {
      p_review_id: reviewId,
      p_action: action,
      p_note: note && note.trim() ? note.trim() : null,
      p_requeue: requeue,
    },
  );
  if (error) {
    throw new Error(friendly(error.message));
  }
  const result = (data ?? {}) as ResolveResult;
  // The RPC reports refusals in the BODY with ok:false rather than as an error, so a
  // caller that only checked `error` would report success on a refusal.
  if (!result.ok) {
    throw new Error(friendly(result.error));
  }
  return result;
}
